import type { _GettersTree, StateTree, StoreDefinition } from 'pinia';
import { defineStore, getActivePinia } from 'pinia';
import { type Actions, type Getters, type State } from './types';

function extractState<T extends Object>(cls: Object): State<T> {
    const stateEntries = Object.entries(Object.getOwnPropertyDescriptors(cls));
    return stateEntries.reduce(
        (prev, [currKey, currValue]) => ({
            ...prev,
            [currKey]: currValue.value,
        }),
        {} as State<T>
    );
}

function extractGetters<T>(cls: Object): Getters<T> {
    const getterEntries = Object.entries(
        Object.getOwnPropertyDescriptors(Object.getPrototypeOf(cls))
    ).filter(([, descriptor]) => typeof descriptor.get === 'function');
    return getterEntries.reduce(
        (prev, [currKey, currValue]) => ({
            ...prev,
            [currKey]: (state: StateTree) => currValue.get?.apply(state),
        }),
        {} as Getters<T>
    );
}

function extractActions<T extends object>(cls: Object): Actions<T> {
    const actionEntries = Object.entries(
        Object.getOwnPropertyDescriptors(Object.getPrototypeOf(cls))
    )
        .filter(([, descriptor]) => typeof descriptor.value === 'function')
        .filter(([key]) => key !== 'constructor');
    return actionEntries.reduce(
        (prev, [currKey, currValue]) => ({
            ...prev,
            [currKey]: currValue.value,
        }),
        {} as Actions<T>
    );
}

export function useStoreIfInstalled<T extends (...args: any) => any>(
    key: string,
    use: Function
) {
    const active = getActivePinia();
    if (active?.state.value[key]) {
        return use() as ReturnType<T>;
    } else {
        return null;
    }
}

interface InstanceClass<T extends () => unknown> {
    use: () => ReturnType<T>;
    useIfInstalled: () => ReturnType<T> | null;
}

export function defineClassStore<Id extends string, T extends () => unknown>(
    id: Id,
    ClassFactory: T
): InstanceClass<T> {
    const use = defineStore(id, {
        state: () => extractState<T>(ClassFactory() as object) as StateTree,
        getters: extractGetters<T>(ClassFactory() as object) as _GettersTree<T>,
        actions: extractActions<T>(ClassFactory() as object),
    }) as StoreDefinition<Id, State<T>, Getters<T>, Actions<T>>;
    return {
        use,
        useIfInstalled: () => useStoreIfInstalled<typeof use>(id, use),
    } as any;
}
